/**
 * Catálogo do domínio `materiais` (T52, CAT-01).
 *
 * Origem: `src/shared/autorizacao/materiais.ts`.
 *
 * ---
 *
 * **O acoplamento oculto, que é o `Done when` desta tarefa.**
 *
 * O arquivo inteiro vive dentro de um `if (user && user.temPermissao(["materiais"]))`
 * (`materiais.ts:6`). Isso quer dizer que **nenhuma** das concessões por relação
 * funciona sozinha: ser a pessoa que quantificou a lista não dá nada a quem não
 * tem também a role `materiais`. O gate do módulo inteiro está pendurado numa
 * role de leitura, e nada no código diz isso — é preciso ler a chave do `if` que
 * está setenta linhas acima.
 *
 * **Aqui ele fica explícito, e não removido:** as três concessões por relação que
 * eram incondicionais dentro do gate (`quantificou`, `verificador`, `coordenador`)
 * moram em `materiais.leitura`, que é o papel para onde `LEGADO.materiais` aponta.
 * Quem tem `materiais.leitura` tem exatamente o que tem hoje; quem não tem,
 * também não tinha. O acoplamento continua existindo, mas agora está escrito.
 *
 * Removê-lo — deixar a relação valer sozinha — **amplia acesso** para quem é
 * responsável por uma lista e não tem a role do módulo. É mudança de
 * comportamento, precisa de aviso à área, e não é desta tarefa.
 *
 * ---
 *
 * **Os dois papéis de criação viraram um: `materiais.criacao` (T56, AD-031 D2).**
 *
 * `materiais_obra` e `materiais_projeto` (`materiais.ts:26,30`) concedem `create`
 * nos dois recursos que a T49 separou (`MateriaisObra` e `MateriaisProjeto`,
 * AD-024). A T53 deixou as duas chaves `null` no `LEGADO` porque a escolha era de
 * negócio, e o usuário a fez: **uma capacidade, dois recursos.**
 *
 * Não é nenhuma das duas saídas que o `PADRONIZACAO-ROLES.md §7b` listou — a
 * capacidade única com condição não fecha (`Concessao.condicao` é fixa por papel),
 * e um nome de capacidade por origem está proibido lá em letra. É a terceira.
 *
 * **A ampliação é o preço, e está aceita:** quem hoje detém só `materiais_obra`
 * passa a criar por projeto também. Vai na lista de avisos à área.
 */
import type { Concessao, NomePapel, Papel } from "../catalogo/vocabulario";

/** Nada é concedido fora do gate de módulo. Ver a nota sobre o acoplamento oculto. */
export const CONCESSOES_BASE_MATERIAIS: readonly Concessao[] = [];

export const PAPEIS_MATERIAIS = {
  "materiais.admin": {
    dominio: "materiais",
    nivel: "admin",
    concessoes: [
      { acoes: ["manage"], recurso: "Materiais" },
      // Os dois recursos precisam ser concedidos explicitamente: `manage` em
      // "Materiais" NAO alcanca outro recurso. Sem estas duas linhas, o
      // administrador perde a criacao que tem hoje — medido em T49.
      { acoes: ["manage"], recurso: "MateriaisObra" },
      { acoes: ["manage"], recurso: "MateriaisProjeto" },
    ],
  },
  "materiais.escrita": {
    dominio: "materiais",
    nivel: "escrita",
    concessoes: [
      { acoes: ["create", "read"], recurso: "Materiais" },
      {
        acoes: ["edit", "liberar"],
        recurso: "Materiais",
        estados: ["REVISAR", "EM ELABORAÇÃO"],
      },
    ],
  },
  /**
   * O papel do gate de módulo. Carrega a leitura **e** as três relações que hoje
   * são incondicionais dentro dele — é onde o acoplamento oculto vira declaração.
   */
  "materiais.leitura": {
    dominio: "materiais",
    nivel: "leitura",
    concessoes: [
      { acoes: ["read"], recurso: "Materiais" },
      {
        acoes: ["edit", "liberar"],
        recurso: "Materiais",
        exigeRelacao: ["quantificou"],
        estados: ["REVISAR", "EM ELABORAÇÃO"],
      },
      {
        acoes: ["liberar"],
        recurso: "Materiais",
        exigeRelacao: ["verificador"],
        estados: ["LIBERADO PARA VERIFICAÇÃO"],
      },
      {
        acoes: ["liberar"],
        recurso: "Materiais",
        exigeRelacao: ["coordenador"],
        estados: ["LIBERADO PARA COORDENADOR"],
      },
    ],
  },
  /**
   * A leitura de materiais do CLIENTE (port de `origin/permissao-materias-para-cliente`
   * fde84ffc, a role `materiais_cliente`).
   *
   * Nivel `leitura_externa`, e nao `materiais.leitura`: este carrega as tres relacoes
   * por registro (quantificou, verificador, coordenador), que sao de equipe Gali e
   * nao fazem sentido para o cliente. So `read`. O original concedia `read` em
   * `Materiais` sem condicao dentro do ramo cliente; aqui o escopo de obra do
   * cliente (`CAMPO_ESCOPO.Materiais`, ADR-0004) passa a restringir a leitura as
   * obras dele — estreitamento deliberado, listado no doc do port.
   *
   * Quem alcanca o papel e `tipo: "cliente"` com a string `materiais_cliente` em
   * `roles` — ver `AMBIGUAS_CLIENTE` em `shared/autorizacao/index.ts`.
   */
  "materiais.leitura_externa": {
    dominio: "materiais",
    nivel: "leitura_externa",
    concessoes: [{ acoes: ["read"], recurso: "Materiais" }],
  },
  /**
   * Criar material, nos dois recursos que a T49 separou (AD-031 D2).
   *
   * Uma capacidade, e não duas: `LEGADO.materiais_obra` e `LEGADO.materiais_projeto`
   * apontam ambos para cá. Ver a nota do topo do arquivo para o porquê e para a
   * ampliação aceita.
   */
  "materiais.criacao": {
    dominio: "materiais",
    nivel: "criacao",
    concessoes: [
      { acoes: ["create"], recurso: "MateriaisObra" },
      { acoes: ["create"], recurso: "MateriaisProjeto" },
    ],
  },
  /**
   * As duas concessões de site manager sobre `Materiais`, agora com dono
   * (`materiais.ts:55-64`, AD-031 D5).
   *
   * Ficam fora de `materiais.leitura` porque hoje exigem **as duas** roles,
   * `materiais` e `site_manager`. Somá-las ao papel de leitura daria a concessão a
   * todo mundo que tem `materiais`.
   *
   * `escrita_propria` é o nível que o `ADR-0004 §D3` criou justamente para isto:
   * escrita restrita à relação, com conjunto de estados próprio.
   * `GRUPOS.SITE_MANAGER` carrega este papel.
   */
  "materiais.escrita_propria": {
    dominio: "materiais",
    nivel: "escrita_propria",
    concessoes: [
      { acoes: ["read"], recurso: "Materiais", exigeRelacao: ["site_manager"] },
      {
        acoes: ["liberar"],
        recurso: "Materiais",
        exigeRelacao: ["site_manager"],
        estados: ["LIBERADO PARA SITE MANAGER"],
      },
    ],
  },
  /** Etapa de workflow de PCP. `GRUPOS.PCP` carrega este papel mais `materiais.leitura`. */
  "materiais.liberacao": {
    dominio: "materiais",
    nivel: "liberacao",
    concessoes: [
      {
        acoes: ["liberar", "editarpcp"],
        recurso: "Materiais",
        estados: ["LIBERADO PARA PLANEJAMENTO"],
      },
    ],
  },
  /**
   * Registrar o recebimento na obra: `Data_real_entrega` e `Numero_nota_fiscal`
   * (adequação de Materiais T33, MAT-17). `Observacao_recebimento` fica aberta a todos.
   *
   * É o `editarObra` do `cbb62e14`, que o original dava a `materiais_obra`. Aqui vira
   * `edit` no recurso `MateriaisObra`, sem ação nova (decisão do usuário: o vocabulário
   * de ações não cresce). `LEGADO.materiais_obra` aponta para cá e para `materiais.criacao`;
   * `materiais.admin` já alcança pelo `manage` em `MateriaisObra`.
   */
  "materiais.recebimento": {
    dominio: "materiais",
    nivel: "recebimento",
    concessoes: [{ acoes: ["edit"], recurso: "MateriaisObra" }],
  },
} as const satisfies Partial<Record<NomePapel, Papel>>;

/**
 * @deprecated Virou o papel `materiais.escrita_propria` na T56 (AD-031 D5).
 *
 * Continua exportada e apontando para o papel para não quebrar quem a importe;
 * some quando `.permissoes-importadores-baseline.json` não listar mais ninguém.
 */
export const CONCESSOES_SITE_MANAGER_MATERIAIS: readonly Concessao[] = [
  ...PAPEIS_MATERIAIS["materiais.escrita_propria"].concessoes,
];
